// Shared low-poly critter parts — the quadruped leg builder that joins a
// creature's legs to its torso. Ported from age-of-mcp's v2/critter-parts.ts:
// the pure geometry is kept verbatim, with the Part type taken from the
// library. The one load-bearing rule it encodes: a quadruped's legs always run
// from the ground (y = 0) up to the BODY'S CENTRE height, so the leg top buries
// into the curved torso and reads as joined at the hip — never a gap under the
// belly. Pure deterministic Part data — no randomness, no DOM.

import * as THREE from "three";
import type { Part } from "../types";

export interface QuadLegOpts {
  /** Stance half-length: a leg pair at each of x = +dx and x = -dx. */
  dx: number;
  /** Stance half-width: legs at z = +dz and z = -dz. */
  dz: number;
  /** Body-centre height — every leg runs from y = 0 up to it (the hip rule). */
  bodyY: number;
  /** Leg radius at the hip. */
  rTop: number;
  /** Leg radius at the foot. */
  rFoot: number;
  color: string;
  /** Optional hoof — a stubby slightly-flared cylinder capping the foot. */
  hoof?: { color: string; r: number; h: number };
}

/** Four legs (+ optional hooves) joined to the torso per the v2 hip rule. */
export function quadLegs(o: QuadLegOpts): Part[] {
  const parts: Part[] = [];
  for (const sx of [1, -1]) {
    for (const sz of [1, -1]) {
      const x = o.dx * sx;
      const z = o.dz * sz;
      parts.push({
        geometry: new THREE.CylinderGeometry(o.rTop, o.rFoot, o.bodyY, 4),
        color: o.color, position: [x, o.bodyY / 2, z],
      });
      if (o.hoof) {
        parts.push({
          geometry: new THREE.CylinderGeometry(o.hoof.r, o.hoof.r * 1.08, o.hoof.h, 5),
          color: o.hoof.color, position: [x, o.hoof.h / 2, z],
        });
      }
    }
  }
  return parts;
}
